import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { listProviderDocumentsAdmin, verifyProviderDocument, rejectProviderDocument, deleteProviderDocument } from "@/lib/admin";
import { ProviderDocumentStatus, ProviderDocumentType, type ProviderDocument } from "@/lib/providerDocuments";

const typeLabelKey: Record<ProviderDocumentType, string> = {
  [ProviderDocumentType.Ine]: "admin.docTypeIne",
  [ProviderDocumentType.ProofOfAddress]: "admin.docTypeProofOfAddress",
  [ProviderDocumentType.Curp]: "admin.docTypeCurp",
  [ProviderDocumentType.ActaConstitutiva]: "admin.docTypeActaConstitutiva",
};

function badge(s: ProviderDocumentStatus, t: (k: string) => string): { label: string; cls: string } {
  if (s === ProviderDocumentStatus.Verified) return { label: t("admin.docVerified"), cls: "bg-emerald-100 text-emerald-700" };
  if (s === ProviderDocumentStatus.Rejected) return { label: t("admin.docRejected"), cls: "bg-coral/10 text-coral" };
  return { label: t("admin.docInReview"), cls: "bg-sand/60 text-charcoal" };
}

export default function DocumentsPanel({ providerId }: { providerId: string }) {
  const { t } = useTranslation("admin");
  const [docs, setDocs] = useState<ProviderDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    listProviderDocumentsAdmin(providerId)
      .then(setDocs)
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [providerId]);

  const verify = async (id: string) => {
    setBusy(id);
    try { await verifyProviderDocument(id); load(); }
    catch { /* surfaced by refresh */ }
    finally { setBusy(null); }
  };
  const reject = async (id: string) => {
    const note = window.prompt(t("admin.docRejectReason")) ?? "";
    setBusy(id);
    try { await rejectProviderDocument(id, note || undefined); load(); }
    catch { /* surfaced by refresh */ }
    finally { setBusy(null); }
  };

  // Destructive and irreversible, so it asks first. Used to clear a wrong or
  // unreadable upload without making the provider do it.
  const remove = async (id: string, fileName: string) => {
    if (!window.confirm(t("admin.docDeleteConfirm", { defaultValue: `¿Eliminar el documento "${fileName}"? Esta acción no se puede deshacer.` }))) return;
    setBusy(id);
    try { await deleteProviderDocument(id); load(); }
    catch { /* surfaced by refresh */ }
    finally { setBusy(null); }
  };

  if (loading) return <div className="text-xs text-gray-400 py-2">{t("admin.docsLoading")}</div>;
  if (docs.length === 0) return <div className="text-xs text-gray-400 py-2">{t("admin.docsEmpty")}</div>;

  return (
    <div className="space-y-2 py-2">
      {docs.map((d) => {
        const b = badge(d.status, t);
        return (
          <div key={d.id} className="flex items-center justify-between gap-3 bg-white rounded-lg px-3 py-2 border border-gray-100">
            <div className="min-w-0 flex items-center gap-2">
              <span className="text-xs font-medium text-charcoal">{t(typeLabelKey[d.type])}</span>
              <a href={d.fileUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-ocean hover:underline truncate max-w-[180px]">{d.fileName}</a>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${b.cls}`}>{b.label}</span>
              {d.status !== ProviderDocumentStatus.Verified && (
                <button onClick={() => verify(d.id)} disabled={busy === d.id} className="text-xs text-emerald-600 hover:underline disabled:opacity-50">{t("admin.docVerify")}</button>
              )}
              {d.status !== ProviderDocumentStatus.Rejected && (
                <button onClick={() => reject(d.id)} disabled={busy === d.id} className="text-xs text-coral hover:underline disabled:opacity-50">{t("admin.docReject")}</button>
              )}
              <button
                onClick={() => remove(d.id, d.fileName)}
                disabled={busy === d.id}
                aria-label={t("admin.docDelete", { defaultValue: "Eliminar documento" })}
                className="text-xs text-gray-400 transition-colors hover:text-coral disabled:opacity-50"
              >
                <i className="ri-delete-bin-line" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
